import NoDataDonutChart from "@/components/common/NoDataDonutChart";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDashboard } from "@/contexts/DashboardContext";
import React, { useState, useEffect } from "react";
import { Cell, Legend, Pie, PieChart, Tooltip as RechartsTooltip, ResponsiveContainer, Label } from 'recharts';
import { Info } from "lucide-react";
import InfoTooltip from "@/components/ui/InfoTooltip";

const COLORS = ['#23407A', '#00FEC1', '#6D8596'];
const DARK_COLORS = ['#1681F6', '#00FEC1', '#6D8596'];

interface InboundCallStatusWidgetData {
  answeredCall: number;
  missedCall: number;
  totalAbandonedCalls: number;
}

interface InboundCallStatusWidgetProps {
  data: InboundCallStatusWidgetData;
  icon?: React.ReactNode;
  loading?: boolean;
  error?: string;
  onClick?: (filters: { [key: string]: string[] }) => void;
}

const InboundCallStatusWidget: React.FC<InboundCallStatusWidgetProps> = ({ data, loading, onClick }) => {
  const { theme } = useDashboard();
  const [showTotal, setShowTotal] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const answered = data.answeredCall || 0;
  const missed = data.missedCall || 0;
  const abandoned = data.totalAbandonedCalls || 0;

  const donutData = [
    { name: 'Answered', value: answered },
    { name: 'Missed', value: missed },
    { name: 'Abandoned', value: abandoned },
  ];

  const total = answered + missed + abandoned;
  const currentColors = theme === 'dark' ? DARK_COLORS : COLORS;

  const handleSegmentClick = (segmentName: string, value: number) => {
    if (onClick && value > 0) {
      onClick({ callStatus: [segmentName] });
    }
  };

  useEffect(() => {
    if (!loading && total > 0) {
      const timer = setTimeout(() => {
        setShowTotal(true);
      }, 1000); // Delay 1 second for animation to complete

      return () => clearTimeout(timer);
    } else {
      setShowTotal(false);
    }
  }, [loading, total]);

  return (
    <div className="w-full min-w-[200px] rounded-lg border flex flex-col bg-white dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] p-3 h-full">
      <div className="flex flex-row items-center gap-2">
        <div className="text-[14x] font-[500] text-primary dark:text-white truncate">Inbound Call Status Distribution</div>
        <InfoTooltip content="The distribution of inbound calls status within the given time." />
      </div>
      <div className="h-full min-h-56">
        {loading ? (
          <Skeleton className="h-full w-full" />
        ) : (
          <div className="flex-1 flex items-center justify-center min-h-[160px]">
            <div className="w-full h-56" style={{ overflow: 'visible' }}>
              {total === 0 ? (
                <NoDataDonutChart
                  width="100%"
                  height="100%"
                  innerRadius={55}
                  outerRadius={70}
                  noDataColor="#E5E7EB"
                  noDataText="No data"
                />
              ) :
                (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={donutData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={70}
                        stroke="none"
                      >
                        {donutData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={currentColors[index % currentColors.length]} 
                            stroke="none"
                            style={{ 
                              cursor: onClick && entry.value > 0 ? 'pointer' : 'default',
                              transformOrigin: 'center',
                              transition: 'transform 0.3s cubic-bezier(0.23, 1, 0.32, 1), filter 0.3s ease-out',
                              transform: hoveredIndex === index ? 'scale(1.04)' : 'scale(1)',
                              filter: hoveredIndex === index 
                                ? 'drop-shadow(0 6px 16px rgba(0,0,0,0.15))' 
                                : 'none',
                              transformBox: 'fill-box',
                              willChange: 'transform, filter'
                            }}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                            onClick={() => handleSegmentClick(entry.name, entry.value)}
                          />
                        ))}
                        {/* {showTotal && (
                          <Label
                            value={total}
                            position="center"
                            style={{
                              fontSize: '24px',
                              fontWeight: 'bold',
                              fill: theme === 'dark' ? '#ffffff' : '#374151'
                            }}
                          />
                        )} */}
                      </Pie>
                      <Legend
                        layout="vertical"
                        verticalAlign="middle"
                        align="right"
                        iconType="rect"
                        wrapperStyle={{ right: 0, bottom: 0, padding: 0, margin: 0, whiteSpace: 'nowrap', width: 'auto', minWidth: 0, height: '100%', display: 'flex', alignItems: 'center' }}
                        formatter={(value) => {
                          const colorIndex = donutData.findIndex(item => item.name === value);
                          const dataItem = donutData.find(item => item.name === value);
                          const canClick = onClick && (dataItem?.value || 0) > 0;
                          return (
                            <span 
                              style={{ 
                                cursor: canClick ? 'pointer' : 'default',
                                userSelect: 'none'
                              }}
                              onClick={() => handleSegmentClick(value, dataItem?.value || 0)}
                            >
                              {value}: {dataItem?.value || 0}
                            </span>
                          );
                        }}
                      />
                      <RechartsTooltip
                        formatter={(value: number, name: string, props: { payload?: { percent?: number; name?: string } }) => {
                          const { payload } = props;
                          const percent = payload && payload.percent ? (payload.percent * 100).toFixed(0) : null;
                          return percent !== null
                            ? [`${value} (${percent}%)`, payload.name]
                            : [value, payload.name];
                        }}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const { name, value } = payload[0].payload;
                            const total = donutData.reduce((sum, item) => sum + item.value, 0);
                            const percent = Number(((value / total) * 100).toFixed(1));
                            return (
                              <div className="bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-600 p-2 rounded-lg">
                                <span>{`${name}: ${value} (${percent}%)`}</span>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InboundCallStatusWidget;
